import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Loader } from "components/Loader";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { TableSection } from "features/Research/v2/components/StockDetailPage/StockDetailPage.styles";
import { Text } from "style/v2/components/Typography";
import {
  Card,
  CardHeader,
  CardContent,
  TitleInfoIcon,
  TooltipText,
} from "./TopHoldings.styles";
import { columns } from "./columns";
import PropTypes from "prop-types";
import { getStockCompanyIcon } from "utils/index";
import { Table } from "components/Table";
import { Tooltip } from "components/Tooltip";
import AllHoldingsModal from "features/Explore/v2/Components/AllHoldingsModal";
import { useAllStrategyHoldings } from "features/Strategy/hook/useAllStrategyHoldings";

const TOP_HOLDINGS_TOOLTIP = (
  <>
    <TooltipText>
      The strategy&apos;s largest positions today, ranked by portfolio weight.
    </TooltipText>
    <TooltipText>
      Share price and industry reflect the latest available data for each
      holding.
    </TooltipText>
    <TooltipText>
      Use Show more to see weight for every asset currently held.
    </TooltipText>
  </>
);

const TopHoldings = ({ data, currentHoldings, isLoading, strategyId }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: allHoldingsData,
    isLoading: isAllHoldingsLoading,
    error: allHoldingsError,
  } = useAllStrategyHoldings(strategyId);

  const allHoldingsWithWeight = useMemo(() => {
    const currentWeights = new Map(
      (currentHoldings || []).map(({ name, value }) => [name, value])
    );
    const rows = (allHoldingsData?.data || []).map((holding) => ({
      ...holding,
      weight: currentWeights.get(holding.asset) ?? null,
    }));
    // Held assets first, largest weight down; Array.sort is stable, so the
    // API's order survives among the ones no longer held.
    return rows.sort(
      (a, b) => (b.weight ?? -Infinity) - (a.weight ?? -Infinity)
    );
  }, [allHoldingsData, currentHoldings]);

  const holdings = useMemo(
    () =>
      [...data]
        .sort((a, b) => (b.value ?? -Infinity) - (a.value ?? -Infinity))
        .map((holding, index) => ({
          id: index + 1,
          asset: {
            symbol: holding.name,
            name: holding.company_name || holding.name,
            imageUrl: getStockCompanyIcon(holding.name),
          },
          weight: holding.value,
          sharePrice: holding.share_price,
          industry: holding.sector,
        })),
    [data]
  );

  // A strategy can have no top_holdings but still hold assets, and this card
  // owns the only link into All Holdings (PG-2758).
  const hasAllHoldings = (allHoldingsData?.data || []).length > 0;

  const handleShowMore = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Held back while the strategy's details load: when the page turns out to
  // be a strategy the viewer may not see, this card unmounts without a toast.
  useEffect(() => {
    if (allHoldingsError && !isLoading) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(allHoldingsError),
        })
      );
    }
  }, [allHoldingsError, isLoading]);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Loader />
        </CardContent>
      </Card>
    );
  }

  // Nothing on the card and nothing behind it -- hide it entirely.
  if (!holdings.length && !isAllHoldingsLoading && !hasAllHoldings) {
    return null;
  }

  return (
    <>
      <TableSection>
        <Card>
          <CardHeader>
            <Text variant="cardHeading" weight="500" as="h3">
              Top holdings
              <Tooltip
                value={TOP_HOLDINGS_TOOLTIP}
                placement="top"
                tooltipClassName="v2-tooltip"
              >
                <TitleInfoIcon size={14} />
              </Tooltip>
            </Text>
            {hasAllHoldings && (
              <a href="#" onClick={handleShowMore}>
                Show more
              </a>
            )}
          </CardHeader>
          <CardContent>
            <Table
              data={holdings}
              columns={columns}
              isLoading={isLoading}
              notFoundText={
                hasAllHoldings
                  ? "No top holdings for this strategy — use Show more to see all holdings"
                  : "No holdings data available"
              }
              showPagination={false}
            />
          </CardContent>
        </Card>
      </TableSection>

      <AllHoldingsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        holdings={allHoldingsWithWeight}
        isLoading={isAllHoldingsLoading}
      />
    </>
  );
};

TopHoldings.propTypes = {
  data: PropTypes.array,
  currentHoldings: PropTypes.array,
  isLoading: PropTypes.bool,
  strategyId: PropTypes.string,
};

export default TopHoldings;
